/**
 * DPDP parental consent — the client's read-only view of the gate.
 *
 * The gate itself lives in the database: a learner under 18 cannot have a
 * single assessment row written until a verified guardian has consented. This
 * module exists so the interface can *explain* that instead of letting someone
 * walk into a wall of failed writes.
 *
 * State comes from one call to `my_consent_state()`, so the browser never
 * assembles the answer from parts it could get wrong.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ConsentState {
  signedIn: boolean;
  /** Age in whole years, or null when no birth date is on record. */
  age: number | null;
  isMinor: boolean;
  /** True when assessment data may lawfully be stored for this learner. */
  allowed: boolean;
  verified: boolean;
  verificationMethod: string | null;
  verifiedAt: string | null;
  consented: boolean;
  consentGrantedAt: string | null;
  noticeVersion: string | null;
  childName: string | null;
  purchaseId: string | null;
  purchaseStage: string | null;
}

const EMPTY: ConsentState = {
  signedIn: false,
  age: null,
  isMinor: false,
  allowed: true,
  verified: false,
  verificationMethod: null,
  verifiedAt: null,
  consented: false,
  consentGrantedAt: null,
  noticeVersion: null,
  childName: null,
  purchaseId: null,
  purchaseStage: null,
};

type RawState = {
  signed_in?: boolean;
  age?: number | string | null;
  is_minor?: boolean;
  allowed?: boolean;
  verified?: boolean;
  verification_method?: string | null;
  verified_at?: string | null;
  consented?: boolean;
  consent_granted_at?: string | null;
  notice_version?: string | null;
  child_name?: string | null;
  purchase_id?: string | null;
  purchase_stage?: string | null;
};

function shape(raw: RawState | null): ConsentState {
  if (!raw?.signed_in) return EMPTY;
  const age = raw.age == null ? null : Number(raw.age);
  return {
    signedIn: true,
    age: Number.isFinite(age as number) ? (age as number) : null,
    isMinor: raw.is_minor === true,
    // Fail closed for a known minor: if the backend didn't say yes, treat the
    // gate as shut rather than sending them into writes that will be refused.
    allowed: raw.is_minor === true ? raw.allowed === true : raw.allowed !== false,
    verified: raw.verified === true,
    verificationMethod: raw.verification_method ?? null,
    verifiedAt: raw.verified_at ?? null,
    consented: raw.consented === true,
    consentGrantedAt: raw.consent_granted_at ?? null,
    noticeVersion: raw.notice_version ?? null,
    childName: raw.child_name ?? null,
    purchaseId: raw.purchase_id ?? null,
    purchaseStage: raw.purchase_stage ?? null,
  };
}

export async function fetchConsentState(): Promise<ConsentState> {
  const { data, error } = await supabase.rpc("my_consent_state");
  if (error) {
    console.warn("consent_state_failed", error.message);
    return EMPTY;
  }
  return shape(data as RawState | null);
}

export function useConsentState() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["consent-state", user?.id ?? "anon"],
    enabled: Boolean(user?.id),
    staleTime: 30 * 1000,
    queryFn: fetchConsentState,
  });
  return {
    state: query.data ?? EMPTY,
    loading: query.isLoading,
    refresh: query.refetch,
  };
}

/**
 * Where a blocked minor must be sent, or null when nothing stands in the way.
 * `next` comes back so the consent screen can return them to what they wanted.
 */
export function consentRedirect(state: ConsentState, next?: string): string | null {
  if (!state.signedIn || !state.isMinor || state.allowed) return null;
  const q = next ? `?next=${encodeURIComponent(next)}` : "";
  return `/passport/consent${q}`;
}

/** True when a failed write was the database gate refusing us. */
export function isConsentBlockedError(error: unknown): boolean {
  const msg =
    typeof error === "string"
      ? error
      : ((error as { message?: string } | null)?.message ?? "");
  return msg.includes("DPDP_CONSENT_REQUIRED");
}

export const CONSENT_NOTICE_VERSION = "2026-08-dpdp-v1";
export const CONSENT_RETENTION_YEARS = 3;
